import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { listAdminUsers, setUserRole } from "@/lib/admin-extra.functions";
import { DataTable, type DataTableColumn } from "@/components/admin/DataTable";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export const Route = createFileRoute("/_authenticated/admin/users")({
  component: UsersPage,
});

type Row = {
  id: string;
  email: string;
  created_at: string;
  last_sign_in_at: string | null;
  confirmed: boolean;
  roles: string[];
};

const when = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" }) : "Never";

function UsersPage() {
  const qc = useQueryClient();
  const list = useServerFn(listAdminUsers);
  const setRole = useServerFn(setUserRole);

  const { data, isPending } = useQuery({ queryKey: ["admin", "users"], queryFn: () => list() });
  const rows = (data ?? []) as Row[];

  const mut = useMutation({
    mutationFn: (v: { userId: string; admin: boolean }) => setRole({ data: v }),
    onSuccess: () => {
      toast.success("Access updated");
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const columns: DataTableColumn<Row>[] = [
    { id: "email", header: "Email", value: (r) => r.email, sortable: true },
    {
      id: "roles",
      header: "Access",
      value: (r) => r.roles.join(", "),
      cell: (r) =>
        r.roles.includes("admin") ? <Badge>Admin</Badge> : <Badge variant="outline">Standard</Badge>,
    },
    {
      id: "confirmed",
      header: "Confirmed",
      value: (r) => (r.confirmed ? "yes" : "no"),
      cell: (r) => (r.confirmed ? "Yes" : "No"),
    },
    { id: "last_sign_in_at", header: "Last sign in", value: (r) => r.last_sign_in_at ?? "", cell: (r) => when(r.last_sign_in_at), sortable: true },
    { id: "created_at", header: "Created", value: (r) => r.created_at, cell: (r) => when(r.created_at), sortable: true },
  ];

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Grant or revoke admin access. Admins can see every booking, patient and order.
      </p>
      <DataTable
        rows={rows}
        columns={columns}
        getRowId={(r) => r.id}
        loading={isPending}
        searchPlaceholder="Search users"
        exportFileName="admin-users"
        emptyMessage="No users yet."
        rowActions={(r) => {
          const isAdminUser = r.roles.includes("admin");
          return (
            <Button
              variant={isAdminUser ? "outline" : "default"}
              size="sm"
              disabled={mut.isPending}
              onClick={() => mut.mutate({ userId: r.id, admin: !isAdminUser })}
            >
              {isAdminUser ? "Revoke admin" : "Make admin"}
            </Button>
          );
        }}
      />
    </div>
  );
}
